/**
 * MenuLoopPlayer - Plays loop.mp3 on the main menu on first load only
 *
 * Behavior:
 * - Plays loop.mp3 on first page load (seamlessly looping)
 * - Stops permanently when:
 *   1. User presses the YouTube music play button, OR
 *   2. User enters the game (presses DRIVE)
 * - Never plays again after being stopped (until full page refresh)
 */

export class MenuLoopPlayer {
    constructor() {
        this.audio = null;
        this.isPlaying = false;
        this.youtubeHasPlayed = false; // Once YouTube plays, loop never returns

        this._createAudio();
    }

    _createAudio() {
        this.audio = new Audio('/assets/bgm/loop.mp3');
        this.audio.loop = true;
        this.audio.volume = 0.5;

        // Handle audio ending (shouldn't happen with loop, but safety)
        this.audio.addEventListener('ended', () => {
            this.isPlaying = false;
        });

        // Handle errors gracefully
        this.audio.addEventListener('error', (e) => {
            console.warn('[MenuLoopPlayer] Audio error:', e);
            this.isPlaying = false;
        });
    }

    /**
     * Start playing the menu loop (only if YouTube hasn't played yet)
     * @param {number} fadeInDuration - Fade-in duration in ms (default 1000)
     */
    play(fadeInDuration = 1000) {
        // Don't play if YouTube has ever been used
        if (this.youtubeHasPlayed) {
            console.log('[MenuLoopPlayer] Skipping - YouTube has been played');
            return;
        }

        if (this.isPlaying) return;

        console.log('[MenuLoopPlayer] Starting menu loop');

        // Start at 0 volume for fade-in
        this.audio.volume = 0;

        const playPromise = this.audio.play();
        if (playPromise) {
            playPromise.then(() => {
                this.isPlaying = true;
                this._fadeIn(fadeInDuration);
            }).catch(e => {
                // Autoplay blocked - this is expected before user interaction
                console.log('[MenuLoopPlayer] Autoplay blocked, waiting for user interaction');
                this.isPlaying = false;
            });
        }
    }

    /**
     * Fade in volume
     */
    _fadeIn(duration) {
        const targetVolume = 0.5;
        const steps = 20;
        const stepDuration = duration / steps;
        const volumeStep = targetVolume / steps;
        let currentStep = 0;

        const fadeInterval = setInterval(() => {
            currentStep++;
            this.audio.volume = Math.min(targetVolume, volumeStep * currentStep);

            if (currentStep >= steps) {
                clearInterval(fadeInterval);
            }
        }, stepDuration);
    }

    /**
     * Stop the menu loop (called when YouTube starts)
     * @param {boolean} markYouTubePlayed - Whether to mark YouTube as having played
     */
    stop(markYouTubePlayed = false) {
        if (markYouTubePlayed) {
            this.youtubeHasPlayed = true;
            console.log('[MenuLoopPlayer] YouTube started - loop disabled for session');
        }

        if (!this.isPlaying) return;

        console.log('[MenuLoopPlayer] Stopping menu loop');
        this.audio.pause();
        this.audio.currentTime = 0;
        this.isPlaying = false;
    }

    /**
     * Set volume (0-1)
     */
    setVolume(volume) {
        this.audio.volume = Math.max(0, Math.min(1, volume));
    }

    /**
     * Check if currently playing
     */
    isCurrentlyPlaying() {
        return this.isPlaying;
    }

    /**
     * Check if YouTube has taken over
     */
    hasYouTubeTakenOver() {
        return this.youtubeHasPlayed;
    }
}
